import React, {FC, ReactNode} from 'react';

import {Trans} from '../../../../libs/shared/core/src/helpers/internationalization/react';
import {CompanyFeaturesEnum} from '../../../../libs/shared/core/src/models/api/company/companyModel';
import {getIsUserGlobalAdmin} from '../../../../libs/shared/core/src/store/data/access/commonAccessSelectors';
import {Banner, BannerTypesEnum} from '../../../../libs/shared/ui/src/components/banners/banner';
import {BannerAction} from '../../../../libs/shared/ui/src/components/banners/bannerAction';
import {BannerContent} from '../../../../libs/shared/ui/src/components/banners/bannerContent';
import {BannerContentTitle} from '../../../../libs/shared/ui/src/components/banners/bannerContentTitle';
import {BannerFooterActions} from '../../../../libs/shared/ui/src/components/banners/bannerFooterActions';
import {BannerIcon} from '../../../../libs/shared/ui/src/components/banners/bannerIcon';
import {BannerPrioritiesEnum} from '../../../../libs/shared/ui/src/components/banners/bannersContext';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {useAppDispatch, useAppSelector} from '../../../helpers/appReduxHelpers';
import {navigateToLinkAction} from '../../../store/stateless/navigationActions';

/*
 * Props.
 */

interface PlanExceededBannerProps {
  feature: CompanyFeaturesEnum;
}

/*
 * Localization.
 */

interface FeatureDefinition {
  name: ReactNode;
  minimumPlan: ReactNode;
  learnMoreLink: string;
}

const featureDefinitions: {[K in CompanyFeaturesEnum]?: FeatureDefinition} = {
  [CompanyFeaturesEnum.ANALYTICS]: {
    name: <Trans id="feature-name-analytics">Analytics</Trans>,
    minimumPlan: <Trans id="feature-plan-prime">Prime</Trans>,
    learnMoreLink: 'https://help.frontapp.com/t/x1244m/understanding-analytics',
  },
  [CompanyFeaturesEnum.SEQUENCES]: {
    name: <Trans id="feature-name-sequences">Sequences</Trans>,
    minimumPlan: <Trans id="feature-plan-plus">Plus</Trans>,
    learnMoreLink: 'https://help.frontapp.com/t/y72911/understanding-sequences',
  },
};

/*
 * Component.
 */

export const PlanExceededBanner: FC<PlanExceededBannerProps> = (props) => {
  const theme = useUserTheme();
  const {feature} = props;

  const isGlobalAdmin = useAppSelector(getIsUserGlobalAdmin);

  const dispatch = useAppDispatch();
  const onOpenBillingClick: () => void = () => dispatch(navigateToLinkAction('/settings/billing'));
  const onOpenFeatureLinkClick: (arg: string) => void = (link) => openInBrowser(link);

  // This banner only works for the features that we have a definition for.
  const featureDefinition = featureDefinitions[feature];
  if (!featureDefinition) {
    return null;
  }

  return (
    <Banner
      bannerType={BannerTypesEnum.MULTILINE}
      color={theme.palette.blue.shade40}
      priority={BannerPrioritiesEnum.TRANSIENT}
    >
      <BannerIcon iconName="gearFilledSmall" />
      <BannerContent>
        <BannerContentTitle>{featureDefinition.name}</BannerContentTitle>
        <Trans id="banner-planexceeded-description">
          This feature is available on the {featureDefinition.minimumPlan} plan and above.
        </Trans>
      </BannerContent>
      <BannerFooterActions>
        <BannerAction onClick={() => onOpenFeatureLinkClick(featureDefinition.learnMoreLink)}>
          <Trans id="banner-planexceeded-learn">Learn more</Trans>
        </BannerAction>
        {isGlobalAdmin && (
          <BannerAction onClick={onOpenBillingClick}>
            <Trans id="banner-planexceeded-upgrade">View billing</Trans>
          </BannerAction>
        )}
      </BannerFooterActions>
    </Banner>
  );
};
